import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import WidgetFrame from "./WidgetFrame";
import EmptyRow from "./EmptyRow";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { fetchSchedules, updateSchedule } from "../../store/agentScheduleSlice";
import type { AppDispatch, RootState } from "../../store";
import type { AgentSchedule } from "../../services/scheduleService";
import { getCurrentUserOwnerCandidates } from "../../utils/ownerScope";
import {
  agentRunNowInput,
  agentToggleInput,
  canManageAgent,
  canRunAgent,
  isAgentOverdue,
  untilShort,
  type AgentRowSummary,
} from "../../utils/operationsDashboard";

interface AgentsWidgetProps {
  agents: AgentRowSummary[];
  isLoading?: boolean;
  /** Receives the saved schedule, so the page patches that one agent instead of refetching the board. */
  onChanged?: (schedule: AgentSchedule) => void;
  className?: string;
}

const VISIBLE_LIMIT = 6;

// The countdown's resolution is a minute, so re-rendering once a minute is enough to flip a row to Overdue
const CLOCK_TICK_MS = 60_000;

type ScheduleUpdate = Parameters<typeof updateSchedule>[0];

// The row's ground carries its state, reusing the tints Alerts uses so erroring stays the only red on the card
const rowTint = (agent: AgentRowSummary) => {
  if (agent.erroring) return "bg-eai-danger-soft";
  if (agent.status === "PAUSED") return "bg-eai-surface-hover";
  return "bg-eai-checklist-lt";
};

// A worded pill, since on this board a pill says "interactive"; active: too, because hover is inert on touch
const CONTROL_CLASS =
  "shrink-0 rounded-full border border-eai-border bg-eai-surface-card px-2 py-0.5 text-center font-outfit text-eai-body-xs font-medium text-eai-ink-faint transition-colors hover:border-eai-cta-primary hover:text-eai-cta-primary active:border-eai-cta-primary active:text-eai-cta-primary disabled:pointer-events-none disabled:opacity-40";

// State, not inventory — a row says when it next runs and whether it is late, never just that it exists.
const statusLabel = (agent: AgentRowSummary, overdue: boolean, now: number) => {
  if (agent.erroring) return "Erroring";
  if (overdue) return "Overdue";
  if (agent.status === "PAUSED") return "Paused";
  // Compact, since "in about 16 hours" truncates the agent's name to nothing on a card this narrow
  return untilShort(agent.nextRunAt, now);
};

const AgentsWidget: React.FC<AgentsWidgetProps> = ({
  agents,
  isLoading,
  onChanged,
  className,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  // Per row, so one agent's request never disables the rest of the list
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // `agent.overdue` is fixed at fetch time, so the rows read against a live clock instead
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Who may write, resolved once: the row shows an org-mate's agent but only its owner can act on it
  const groups: string[] =
    useSelector((state: RootState) => (state.user?.details as { groups?: string[] } | undefined)?.groups) ?? [];
  const [ownerCandidates, setOwnerCandidates] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    getCurrentUserOwnerCandidates().then((candidates) => {
      if (live) setOwnerCandidates(candidates);
    });
    return () => {
      live = false;
    };
  }, []);

  // The analyst's own Manage Agents modal reads the Redux list, which this page's React Query fetch never fills — seed it first
  const manage = () => {
    dispatch(fetchSchedules());
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.manageSchedules,
        properties: { __modalSize: "md" },
      })
    );
  };

  const act = async (agent: AgentRowSummary, input: ScheduleUpdate, failure: string) => {
    if (pendingIds.includes(agent.id)) return;
    setError(null);
    setPendingIds((prev) => [...prev, agent.id]);
    try {
      const saved = await dispatch(updateSchedule(input)).unwrap();
      onChanged?.(saved);
    } catch {
      setError(failure);
    } finally {
      setPendingIds((prev) => prev.filter((id) => id !== agent.id));
    }
  };

  return (
    <WidgetFrame
      title="Agents"
      count={agents.length}
      isLoading={isLoading}
      padBody={false}
      scrollBody
      className={className}
      actionLabel="Manage"
      onAction={manage}
    >
      {agents.length === 0 ? (
        <div className="px-5 py-3">
          <EmptyRow>No agent schedules yet.</EmptyRow>
        </div>
      ) : (
        /* px-3 + the row's own px-2 puts the text on the header's 20px content left, as Alerts does */
        <div className="flex flex-col gap-1 px-3 py-2">
          {error && (
            <p className="px-2 pb-1 font-inter text-eai-body-xs text-eai-danger-text">{error}</p>
          )}
          {agents.slice(0, VISIBLE_LIMIT).map((agent) => {
            const active = agent.status === "ACTIVE";
            const busy = pendingIds.includes(agent.id);
            const canManage = canManageAgent(agent, ownerCandidates, groups);
            const overdue = agent.overdue || isAgentOverdue(agent, now);

            return (
              <div
                key={agent.id}
                /* No hover border: the row itself does nothing now, and only the controls in it are interactive */
                className={`flex items-center gap-2 rounded-md px-2 py-2 ${rowTint(agent)}`}
              >
                {/* A live dot reads faster than a pill, and the design uses it for exactly this */}
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    agent.erroring
                      ? "bg-eai-danger"
                      : overdue
                      ? "bg-eai-cta-primary"
                      : active
                      ? "bg-eai-cta-primary"
                      : "bg-eai-ink-ghost"
                  }`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  {/* Titled, since the two controls beside it can truncate a long name on this column's width */}
                  <span
                    title={agent.title}
                    className="block truncate font-inter text-eai-body-sm font-medium text-eai-ink-soft"
                  >
                    {agent.title}
                  </span>
                  {/* Cadence and state on one line, so the right edge belongs to the controls; run count lives in the modal */}
                  <span className="mt-0.5 block truncate font-inter text-eai-body-xs text-eai-ink-faint">
                    {agent.cadence} ·{" "}
                    <span
                      className={
                        agent.erroring
                          ? "font-medium text-eai-danger"
                          : overdue
                          ? "font-medium text-eai-cta-primary"
                          : ""
                      }
                    >
                      {statusLabel(agent, overdue, now)}
                    </span>
                  </span>
                </div>

                {/* Standing, not hover-revealed, and dropped without write access — a control that always fails is worse than none */}
                {canManage && (
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      act(agent, agentToggleInput(agent), "Couldn’t update that agent.")
                    }
                    title={active ? "Pause agent" : "Resume agent"}
                    aria-label={`${active ? "Pause" : "Resume"} ${agent.title}`}
                    className={CONTROL_CLASS}
                  >
                    {active ? "Pause" : "Resume"}
                  </button>
                  {/* Only on a live agent: the poller ignores paused schedules, so "Run" there used to mean "resume forever" */}
                  {canRunAgent(agent) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act(agent, agentRunNowInput(agent), "Couldn’t run that agent.")}
                      title="Run once"
                      aria-label={`Run ${agent.title} once`}
                      className={CONTROL_CLASS}
                    >
                      Run
                    </button>
                  )}
                </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </WidgetFrame>
  );
};

export default AgentsWidget;
